import type { ViewerProps } from '@ls-group/3dmc-viewer/dist/3dmc-viewer';
import type { ComponentType } from 'react';
import { useEffect, useRef, useState } from 'react';

import type { Maybe } from '@/lib/types';

import * as S from './Viewer3D.styles';

export interface Viewer3DProps {
  assetPath: string;
  className?: string;
  height?: string;
  modelName: string;
  onError?: (error: Error) => void;
  onLoadingChange?: (isLoading: boolean) => void;
  width?: string;
}

export const Viewer3D = ({
  assetPath,
  className,
  height = '100%',
  modelName,
  onError,
  onLoadingChange,
  width = '100%',
}: Viewer3DProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Maybe<Error>>(null);
  const [ViewerComponent, setViewerComponent] =
    useState<ComponentType<ViewerProps> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Dynamically import the 3DMC viewer
  useEffect(() => {
    let isMounted = true;

    const loadViewer = async () => {
      try {
        setIsLoading(true);
        onLoadingChange?.(true);

        // Dynamically import the viewer component
        const { Viewer } =
          await import('@ls-group/3dmc-viewer/dist/3dmc-viewer');

        if (isMounted) {
          setViewerComponent(() => Viewer);
          setIsLoading(false);
          onLoadingChange?.(false);
        }
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error('Failed to load 3D viewer');
        console.error('Error loading 3D viewer:', error);

        if (isMounted) {
          setError(error);
          setIsLoading(false);
          onLoadingChange?.(false);
          onError?.(error);
        }
      }
    };

    loadViewer();

    return () => {
      isMounted = false;
    };
  }, [onLoadingChange, onError]);

  if (error) {
    return (
      <S.Container
        ref={containerRef}
        className={className}
        style={{ height, width }}
      >
        <S.ErrorContainer>
          <S.ErrorMessage>Failed to load 3D viewer</S.ErrorMessage>
          <S.ErrorDetails>{error.message}</S.ErrorDetails>
        </S.ErrorContainer>
      </S.Container>
    );
  }

  if (isLoading || !ViewerComponent) {
    return (
      <S.Container
        ref={containerRef}
        className={className}
        style={{ height, width }}
      >
        <S.LoadingContainer>
          <S.LoadingSpinner />
          <S.LoadingText>Loading 3D viewer...</S.LoadingText>
        </S.LoadingContainer>
      </S.Container>
    );
  }

  return (
    <S.Container
      ref={containerRef}
      className={className}
      data-testid='viewer3d'
      style={{ height, width }}
    >
      <ViewerComponent
        key={modelName}
        assetPath={assetPath}
        modelName={modelName}
      />
    </S.Container>
  );
};

Viewer3D.displayName = 'Viewer3D';
